import styled from 'styled-components';
import {getColorFromTheme} from '../../ui-utils';

export const FilterAnchor = styled.div`
  position: relative;
  flex: 0 0 auto;

  @media (max-width: 520px) {
    width: 100%;
  }
`;

export const FilterTrigger = styled.button<{$active: boolean; $open: boolean}>`
  display: inline-flex;
  height: 44px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 7px 12px;
  border: 1px solid
    ${({$active}) =>
      $active
        ? getColorFromTheme('purple300')
        : getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${({$active}) =>
    $active
      ? getColorFromTheme('backgroundLight')
      : getColorFromTheme('white')};
  color: ${({$active}) =>
    $active ? getColorFromTheme('purple300') : getColorFromTheme('textBody')};
  font: 600 13px/20px Graphik, sans-serif;
  white-space: nowrap;

  svg {
    flex: 0 0 auto;
  }

  > svg:last-child {
    transform: rotate(${({$open}) => ($open ? '180deg' : '0deg')});
    transition: transform 160ms ease;
  }

  &:hover,
  &:focus-visible {
    outline: none;
    border-color: ${getColorFromTheme('purple300')};
    color: ${getColorFromTheme('purple300')};
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px ${getColorFromTheme('purple200')};
  }

  &:disabled {
    cursor: not-allowed;
    border-color: ${getColorFromTheme('borderLight')};
    background: ${getColorFromTheme('backgroundDark')};
    color: ${getColorFromTheme('textMuted')};
  }

  @media (max-width: 520px) {
    width: 100%;
  }
`;

export const FilterCount = styled.span`
  display: grid;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  place-items: center;
  border-radius: 999px;
  background: ${getColorFromTheme('purple300')};
  color: ${getColorFromTheme('alwaysWhite')};
  font: 600 11px/18px Graphik, sans-serif;
`;

export const FilterPanel = styled.div`
  position: absolute;
  z-index: 40;
  top: calc(100% + 8px);
  right: 0;
  display: flex;
  width: 300px;
  max-height: min(480px, calc(100vh - 96px));
  flex-direction: column;
  overflow: hidden;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  background: ${getColorFromTheme('white')};
  box-shadow: ${({theme}) => theme.elevation.level2};

  @media (max-width: 520px) {
    position: fixed;
    right: 16px;
    bottom: 16px;
    left: 16px;
    top: auto;
    width: auto;
    max-height: calc(100vh - 32px);
  }
`;

export const FilterHeader = styled.div`
  display: flex;
  min-height: 44px;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  padding: 10px 14px;
  border-bottom: 1px solid ${getColorFromTheme('borderDark')};

  strong {
    color: ${getColorFromTheme('blackTwo')};
    font: 600 14px/22px Graphik, sans-serif;
  }
`;

export const FilterTextButton = styled.button<{$active?: boolean}>`
  padding: 2px;
  border: 0;
  outline: none;
  background: transparent;
  color: ${({$active}) =>
    $active ? getColorFromTheme('purple300') : getColorFromTheme('textBody')};
  font: 500 12px/20px Graphik, sans-serif;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: ${getColorFromTheme('purple300')};
  }

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple200')};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    color: ${getColorFromTheme('textMuted')};
  }
`;

export const FilterBody = styled.div`
  min-height: 0;
  overflow-y: auto;
  padding: 0 16px;
`;

export const FilterSection = styled.section`
  padding: 10px 0;
  border-top: 1px solid ${getColorFromTheme('borderLight')};

  &:first-child {
    border-top: 0;
  }
`;

export const FilterSectionTitle = styled.h3`
  margin: 0 0 6px;
  color: ${getColorFromTheme('textBody')};
  font: 600 11px/18px Graphik, sans-serif;
  text-transform: uppercase;
`;

export const FilterSearch = styled.div`
  position: relative;
  margin-bottom: 6px;

  input {
    width: 100%;
    height: 36px;
    padding: 7px 34px 7px 10px;
    border: 1px solid ${getColorFromTheme('borderDark')};
    border-radius: 7px;
    outline: none;
    color: ${getColorFromTheme('blackTwo')};
    font: 400 13px/20px Graphik, sans-serif;

    &::placeholder {
      color: ${getColorFromTheme('textBody')};
    }

    &:focus {
      border-color: ${getColorFromTheme('purple300')};
      box-shadow: 0 0 0 3px ${getColorFromTheme('purple200')};
    }
  }

  svg {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 16px;
    height: 16px;
    pointer-events: none;
  }
`;

export const FilterOptions = styled.div`
  max-height: 176px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-color: ${getColorFromTheme('borderDark')} transparent;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 6px;
    background: ${getColorFromTheme('borderDark')};
  }
`;

export const FilterOption = styled.label`
  display: flex;
  min-height: 36px;
  cursor: pointer;
  align-items: center;
  gap: 8px;
  padding: 4px;
  border-radius: 6px;
  color: ${getColorFromTheme('blackTwo')};
  font: 400 13px/20px Graphik, sans-serif;

  &:hover {
    background: ${getColorFromTheme('backgroundLight')};
  }

  &:focus-within {
    outline: 2px solid ${getColorFromTheme('purple200')};
    outline-offset: -2px;
  }
`;

export const FilterOptionCopy = styled.span`
  min-width: 0;
  flex: 1;
`;

export const FilterEmpty = styled.p`
  margin: 8px 4px;
  color: ${getColorFromTheme('textMuted')};
  font: 400 12px/20px Graphik, sans-serif;
`;

export const FilterFooter = styled.div`
  display: flex;
  min-height: 60px;
  align-items: center;
  flex: 0 0 auto;
  padding: 12px 14px;
  border-top: 1px solid ${getColorFromTheme('borderDark')};
  background: ${getColorFromTheme('white')};
`;

export const AppliedFilterRow = styled.div`
  display: flex;
  min-height: 42px;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
`;

export const AppliedFilterLabel = styled.span`
  color: ${getColorFromTheme('textBody')};
  font: 400 12px/20px Graphik, sans-serif;
`;

export const AppliedFilterChip = styled.button`
  display: inline-flex;
  min-height: 26px;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border: 0;
  border-radius: 6px;
  outline: none;
  background: ${getColorFromTheme('purple200')};
  color: ${getColorFromTheme('purple300')};
  font: 500 12px/20px Graphik, sans-serif;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: ${getColorFromTheme('backgroundDark')};
  }

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: 1px;
  }
`;
